import { Theme, useTheme } from "@emotion/react";
import { AnimatePresence, motion, MotionValue } from "motion/react";
import { createPortal } from "react-dom";

import { AssigneeRow } from "./supportTicketAssigneeRow";

type Props = {
  mounted: boolean;
  hoveredEmail: string | null;
  coords: { x: number; y: number } | null;
  hoveredRow: AssigneeRow | undefined;
  translateX: MotionValue<number>;
  rotate: MotionValue<number>;
};

function TooltipDecorations({ theme }: { theme: Theme }) {
  return (
    <>
      <div
        css={{
          position: "absolute",
          left: "1rem",
          right: "1rem",
          bottom: -1,
          height: 1,
          width: "20%",
          margin: "0 auto",
          background: `linear-gradient(90deg, transparent, ${theme.colors.success}, transparent)`,
        }}
      />
      <div
        css={{
          position: "absolute",
          left: "1rem",
          bottom: -1,
          height: 1,
          width: "40%",
          background: `linear-gradient(90deg, transparent, hsl(199, 89%, 48%), transparent)`,
        }}
      />
    </>
  );
}

export function SupportTicketAssigneesTooltip({
  mounted,
  hoveredEmail,
  coords,
  hoveredRow,
  translateX,
  rotate,
}: Props) {
  const theme = useTheme();

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {hoveredEmail && coords && hoveredRow ? (
        <motion.div
          key={hoveredEmail}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: { duration: 0.18 },
          }}
          exit={{ opacity: 0 }}
          css={{
            position: "fixed",
            left: coords.x,
            top: coords.y,
            transform: "translate(-50%, calc(-100% - 10px))",
            zIndex: 10000,
            pointerEvents: "none",
          }}
        >
          <motion.div
            style={{
              translateX,
              rotate,
            }}
            css={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "0.25rem 0.5rem",
              borderRadius: theme.extraSmallBorderRadius,
              background: theme.colors.default,
              color: theme.colors.background,
              boxShadow: "0 6px 14px rgba(0,0,0,0.2)",
              whiteSpace: "nowrap",
              maxWidth: "min(220px, calc(100vw - 24px))",
              transformOrigin: "bottom center",
            }}
          >
            <TooltipDecorations theme={theme} />
            <div
              css={{
                position: "relative",
                zIndex: 1,
                fontFamily: theme.displayFontFamily,
                fontSize: "0.8125rem",
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {hoveredRow.fullName}
            </div>
            <div
              css={{
                position: "relative",
                zIndex: 1,
                fontSize: "0.6875rem",
                color: theme.colors.background,
                opacity: 0.88,
                marginTop: "0.0625rem",
                lineHeight: 1.2,
              }}
            >
              {hoveredRow.roleLine}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
